<script lang="ts">
	import { createEventDispatcher } from 'svelte';
	import { format } from 'svelte-i18n';
	import type { Device } from 'src/types/Device';
	import { Button, Input, Modal, Text } from '@Shelly/shelly-design-system/index';
	import type { NfsConfig } from 'src/components/Camera/types/camera-storage';
	import { setStorageNfsConfig } from 'src/components/Camera/helpers/camera-storage-helpers';

	export let device: Device;
	export let showModal = false;
	export let nfs: NfsConfig | undefined;

	const dispatch = createEventDispatcher();

	let host = '';
	let path = '';
	let loading = false;

	$: if (showModal) seed(nfs);

	const seed = (config: NfsConfig | undefined) => {
		host = config?.host ?? '';
		path = config?.path ?? '';
	};

	const close = () => {
		dispatch('close');
	};

	const save = async () => {
		loading = true;
		const trimmedHost = host.trim();
		const trimmedPath = path.trim();
		const nextNfs =
			trimmedHost || trimmedPath ? { host: trimmedHost || null, path: trimmedPath || null } : null;
		const success = await setStorageNfsConfig(
			device,
			nextNfs,
			$format('camera_storage_nfs_saved_toast', { default: 'NFS settings saved.' }),
		);
		loading = false;
		if (success) close();
	};
</script>

<Modal
	{showModal}
	title={$format('camera_storage_nfs_modal_title', { default: 'Set up NFS' })}
	backdropClose={false}
	on:close={close}
>
	<svelte:fragment slot="content">
		<Text styleType="body-small" class="text-color-muted mb-s">
			{$format('camera_storage_nfs_modal_desc', {
				default: 'Mount a network share to store recordings. Leave empty to use the SD card.',
			})}
		</Text>
		<Input
			id="camera-storage-nfs-host"
			class="mb-s"
			icon="fas fa-server"
			label={$format('camera_storage_nfs_host', { default: 'NFS host' })}
			aria-label={$format('camera_storage_nfs_host', { default: 'NFS host' })}
			disabled={loading}
			bind:value={host}
		/>
		<Input
			id="camera-storage-nfs-path"
			icon="fas fa-folder"
			label={$format('camera_storage_nfs_path', { default: 'NFS path' })}
			aria-label={$format('camera_storage_nfs_path', { default: 'NFS path' })}
			disabled={loading}
			bind:value={path}
		/>
	</svelte:fragment>
	<div slot="footer" class="d-flex justify-content-end">
		<Button
			class="mr-s"
			variant="secondary"
			disabled={loading}
			on:click={close}
			aria-label={$format('button_cancel', { default: 'Cancel' })}
		>
			{$format('button_cancel', { default: 'Cancel' })}
		</Button>
		<Button {loading} on:click={save} aria-label={$format('button_save', { default: 'Save' })}>
			{$format('button_save', { default: 'Save' })}
		</Button>
	</div>
</Modal>
